import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import { ccApi } from '../../services/api';
import { usePlatoonStore } from '../../store/platoonStore';
import { useRealtimeStore } from '../../store/realtimeStore';
import { useCcStore } from '../../store/ccStore';
import type { CommsStatus } from '../../types/domain';

const DARK_MS = 5 * 60 * 1000;
// The field app currently sends a placeholder -70; only show signal if it looks real.
const PLACEHOLDER_SIGNAL = -70;

export default function CommsPanel() {
  const { platoons } = usePlatoonStore();
  const { locations } = useRealtimeStore();
  const commsDark = useCcStore((s) => s.commsDark);

  const statusQ = useQuery({
    queryKey: ['comms-status'],
    queryFn: async () => (await ccApi.getCommsStatus()).data.platoons as CommsStatus[],
    retry: false,
    refetchInterval: 15000,
  });

  // Prefer the endpoint; fall back to deriving from platoon list + live locations.
  const rows = useMemo<CommsStatus[]>(() => {
    if (statusQ.data && statusQ.data.length) {
      return statusQ.data.map((c) => ({ ...c, dark: c.dark ?? isDark(c.lastSeenAt) }));
    }
    const locByPlatoon = new Map<string, string>();
    for (const loc of locations.values()) {
      const pid = loc.platoonId || loc.platoon?.id;
      if (pid) locByPlatoon.set(pid, loc.serverTime);
    }
    return platoons.map((p) => {
      const lastSeenAt = p.device?.lastSeenAt ?? locByPlatoon.get(p.id) ?? null;
      return {
        platoonId: p.id,
        callsign: p.callsign,
        unitId: p.unitId,
        lastSeenAt,
        batteryLevel: p.device?.batteryLevel ?? null,
        signalStrength: p.device?.signalStrength ?? null,
        dark: commsDark[p.id] ?? isDark(lastSeenAt),
      };
    });
  }, [statusQ.data, platoons, locations, commsDark]);

  const dark = rows.filter((r) => r.dark);

  return (
    <div className="rounded-xl border border-command-border bg-command-panel p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-100">Who's Dark</h3>
        <span className={clsx('text-xs font-semibold', dark.length ? 'text-amber-400' : 'text-green-400')}>
          {dark.length} dark / {rows.length}
        </span>
      </div>
      <ul className="space-y-1">
        {rows.map((r) => {
          const showSignal = r.signalStrength != null && r.signalStrength !== PLACEHOLDER_SIGNAL;
          return (
            <li key={r.platoonId} className={clsx('flex items-center justify-between px-2 py-1.5 rounded text-xs', r.dark ? 'bg-red-500/10' : 'bg-command-bg/40')}>
              <span className="flex items-center gap-2">
                <span className={clsx('w-2 h-2 rounded-full', r.dark ? 'bg-red-500 animate-pulse' : 'bg-green-500')} />
                <span className="text-gray-200 font-semibold">{r.callsign ?? r.platoonId?.slice(0, 6) ?? '—'}</span>
              </span>
              <span className="text-gray-400 flex items-center gap-3">
                {r.batteryLevel != null && <span>🔋 {r.batteryLevel}%</span>}
                {showSignal && <span>📶 {r.signalStrength}</span>}
                <span className={clsx(r.dark && 'text-red-400')}>
                  {r.lastSeenAt ? formatDistanceToNow(new Date(r.lastSeenAt), { addSuffix: true }) : 'never'}
                </span>
              </span>
            </li>
          );
        })}
        {rows.length === 0 && <li className="text-[11px] text-gray-500 py-4 text-center">No platoons tracked.</li>}
      </ul>
    </div>
  );
}

function isDark(lastSeenAt?: string | null): boolean {
  if (!lastSeenAt) return true;
  const t = new Date(lastSeenAt).getTime();
  if (Number.isNaN(t)) return true;
  return Date.now() - t > DARK_MS;
}
